import React, { useMemo } from "react";
import {
    Bar,
    BarRow,
    Card,
    Donut,
    EmptyState,
    LegendDot,
    LineChart,
    PALETTE,
    SERIES_COLORS,
    formatCount,
    formatCurrency,
    formatMinutes,
    formatPercent,
} from "./Primitives";
import { formatHotel } from "../../../api/hotelTime";

const bucketLabel = (bucket, granularity) => {
    if (granularity === "month") return formatHotel(bucket, "MMM");
    if (granularity === "week") return formatHotel(bucket, "DD MMM");
    return formatHotel(bucket, "DD MMM");
};

const closureColor = (minutes) => {
    if (minutes > 45) return PALETTE.red;
    if (minutes > 28) return PALETTE.amber;
    return PALETTE.green;
};

const ratingColor = (rating) => {
    if (rating >= 4.5) return PALETTE.green;
    if (rating >= 4) return PALETTE.amber;
    return PALETTE.red;
};

export default function OverviewTab({ summary, requests, trends, granularity }) {
    const series = useMemo(
        () =>
            (trends?.trend ?? []).map((row) => ({
                label: bucketLabel(row.bucket, granularity),
                revenue: row.revenue,
                upsell: row.upsell_revenue,
                bookings: row.bookings,
                requests: row.requests,
            })),
        [trends, granularity],
    );

    const byType = useMemo(() => requests?.by_type ?? [], [requests]);
    const maxCount = Math.max(...byType.map((row) => row.total), 1);

    const closureRows = useMemo(
        () => [...byType].filter((row) => row.avg_closure_minutes > 0).sort((a, b) => a.avg_closure_minutes - b.avg_closure_minutes),
        [byType],
    );
    const maxClosure = Math.max(...closureRows.map((row) => row.avg_closure_minutes), 1);

    const ratedRows = useMemo(
        () => [...byType].filter((row) => row.rating_count > 0).sort((a, b) => b.avg_rating - a.avg_rating),
        [byType],
    );

    const services = trends?.by_service ?? [];
    const maxService = Math.max(...services.map((row) => row.revenue), 1);
    const serviceTotal = services.reduce((sum, row) => sum + row.revenue, 0);

    const engagement = summary?.engagement ?? {};
    const requestStats = summary?.requests ?? {};

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Card title="Upsell Revenue Trend" extra={<LegendDot color={PALETTE.red} label="Upsell Revenue" />}>
                <LineChart
                    data={series}
                    lines={[
                        {
                            key: "upsell",
                            color: PALETTE.red,
                            label: "upsell revenue",
                            format: formatCurrency,
                        },
                    ]}
                />
            </Card>

            <Card title="Requests by Type">
                {byType.length === 0 ? (
                    <EmptyState message="No requests in this period" />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {byType.slice(0, 9).map((row) => (
                            <BarRow
                                key={row.type}
                                label={row.type}
                                value={row.total}
                                max={maxCount}
                                color={PALETTE.blue}
                                display={row.total}
                                details={[
                                    { color: PALETTE.blue, label: "requests", value: row.total },
                                    { color: PALETTE.green, label: "completed", value: row.completed },
                                    { color: PALETTE.red, label: "declined", value: row.missed },
                                ]}
                            />
                        ))}
                    </div>
                )}
            </Card>

            <Card
                title="Bookings & Requests Trend"
                extra={
                    <div style={{ display: "flex", gap: 12 }}>
                        <LegendDot color={PALETTE.green} label="Booked Rooms" />
                        <LegendDot color={PALETTE.amber} label="Requests" />
                    </div>
                }
            >
                <LineChart
                    data={series}
                    lines={[
                        {
                            key: "bookings",
                            color: PALETTE.green,
                            label: "booked rooms",
                            format: formatCount,
                        },
                        { key: "requests", color: PALETTE.amber, label: "requests", format: formatCount },
                    ]}
                />
            </Card>

            <Card title="Avg Closure Time by Request Type">
                {closureRows.length === 0 ? (
                    <EmptyState message="No closed requests in this period" />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {closureRows.slice(0, 9).map((row) => (
                            <BarRow
                                key={row.type}
                                label={row.type}
                                value={row.avg_closure_minutes}
                                max={maxClosure}
                                color={closureColor(row.avg_closure_minutes)}
                                display={formatMinutes(row.avg_closure_minutes)}
                                details={[
                                    {
                                        color: closureColor(row.avg_closure_minutes),
                                        label: "avg closure",
                                        value: formatMinutes(row.avg_closure_minutes),
                                    },
                                    { label: "closed requests", value: row.completed },
                                ]}
                            />
                        ))}
                    </div>
                )}
            </Card>

            <Card title="Guest Loyalty">
                <div style={{ display: "flex", alignItems: "center", gap: 20, paddingBottom: 6 }}>
                    <Donut
                        segments={[
                            {
                                value: engagement.repeat_guests ?? 0,
                                color: PALETTE.purple,
                                label: "returning guests",
                            },
                            {
                                value: engagement.new_guests ?? 0,
                                color: "#DDE2EE",
                                label: "new guests",
                            },
                        ]}
                    />
                    <div>
                        <div style={{ fontSize: 28, fontWeight: 700, color: PALETTE.purple }}>
                            {formatPercent(engagement.repeat_rate)}
                        </div>
                        <div style={{ fontSize: 12.5, color: "#69708A" }}>Repeat guest rate</div>
                        <div style={{ marginTop: 8, display: "flex", gap: 20 }}>
                            <div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>
                                    {engagement.repeat_guests ?? 0}
                                </div>
                                <div style={{ fontSize: 11, color: "#9097ac" }}>Returning</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>
                                    {engagement.new_guests ?? 0}
                                </div>
                                <div style={{ fontSize: 11, color: "#9097ac" }}>New guests</div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            <Card title="Revenue Loss from Missed Requests">
                <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 26, fontWeight: 700, color: PALETTE.red }}>
                        {formatCurrency(summary?.revenue_lost)}
                    </div>
                    <div style={{ fontSize: 12, color: "#69708A", marginTop: 2 }}>
                        {requestStats.missed ?? 0} requests declined during this period
                    </div>
                </div>
                <div style={{ fontSize: 11, color: "#9097ac", marginBottom: 6 }}>Request completion rate</div>
                <div style={{ marginBottom: 5 }}>
                    <Bar value={requestStats.completion_rate ?? 0} max={100} color={PALETTE.green} height={10} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11.5, color: PALETTE.green, fontWeight: 600 }}>
                        {formatPercent(requestStats.completion_rate)} completed
                    </span>
                    <span style={{ fontSize: 11.5, color: "#9097ac" }}>
                        {requestStats.completed ?? 0} of {requestStats.total ?? 0}
                    </span>
                </div>
            </Card>

            <Card
                title="Service Ratings Across Services"
                extra={
                    ratedRows.length > 0 && (
                        <span style={{ fontSize: 11, color: "#9097ac" }}>
                            {ratedRows.reduce((sum, row) => sum + row.rating_count, 0)} ratings
                        </span>
                    )
                }
            >
                {ratedRows.length === 0 ? (
                    <EmptyState message="No ratings collected in this period" />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {ratedRows.map((row) => (
                            <BarRow
                                key={row.type}
                                label={row.type}
                                value={row.avg_rating}
                                max={5}
                                color={ratingColor(row.avg_rating)}
                                display={`${row.avg_rating.toFixed(1)}★`}
                                details={[
                                    {
                                        color: ratingColor(row.avg_rating),
                                        label: "avg rating",
                                        value: `${row.avg_rating.toFixed(1)} / 5`,
                                    },
                                    { label: "ratings given", value: row.rating_count },
                                ]}
                            />
                        ))}
                    </div>
                )}
            </Card>

            <Card
                title="Revenue Split Across Services"
                extra={
                    <span style={{ fontSize: 11, color: "#9097ac" }}>{formatCurrency(serviceTotal)} total</span>
                }
            >
                {services.length === 0 ? (
                    <EmptyState message="No service revenue in this period" />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                        {services.map((row, index) => (
                            <BarRow
                                key={row.service}
                                label={row.service}
                                value={row.revenue}
                                max={maxService}
                                color={SERIES_COLORS[index % SERIES_COLORS.length]}
                                labelWidth={120}
                                displayWidth={74}
                                display={formatCurrency(row.revenue)}
                                details={[
                                    {
                                        color: SERIES_COLORS[index % SERIES_COLORS.length],
                                        label: "revenue",
                                        value: formatCurrency(row.revenue),
                                    },
                                    { label: "share of revenue", value: formatPercent(row.percentage) },
                                    { label: "paid requests", value: row.requests },
                                ]}
                                trailing={
                                    <span
                                        style={{
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            color: SERIES_COLORS[index % SERIES_COLORS.length],
                                            width: 38,
                                            textAlign: "right",
                                        }}
                                    >
                                        {formatPercent(row.percentage)}
                                    </span>
                                }
                            />
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
